/**
 * Payment Handler Registry.
 * Provide a registry-based pattern for routing payment actions to the correct handler
 * based on payment type and engine/provider context.
 *
 * Each handler implements:
 * - canHandle(context): Optional predicate for sub-routing within a payment type.
 * - execute(context): Async function that performs the payment action.
 * - priority: Optional number (default 0). Higher-priority handlers are asked first.
 *
 * Resolution order:
 * 1. First handler whose canHandle(context) returns true, highest priority first and then in
 *    registration order. The Basis Theory card handler is registered with a higher priority, so
 *    an engine handler whose predicate forgets `cardCheckoutMode` cannot take its submissions.
 * 2. If none match, the first handler without canHandle (fallback).
 */

const registry = new Map();

/**
 * Register a payment handler for a given payment type.
 * @param {string} paymentType - The payment type (e.g., 'credit_card', 'transfer').
 * @param {object} handler - The handler object with execute and optional canHandle.
 * @param {Function} [handler.canHandle] - Predicate receiving context, returns boolean.
 * @param {Function} handler.execute - Async function receiving context, performs payment.
 * @param {number} [handler.priority=0] - Resolution priority among `canHandle` handlers.
 */
function registerHandler(paymentType, handler) {
  if (!registry.has(paymentType)) {
    registry.set(paymentType, []);
  }
  registry.get(paymentType).push(handler);
}

/**
 * Get the appropriate handler for a payment type and context.
 * @param {string} paymentType - The payment type to look up.
 * @param {object} context - Context object passed to canHandle predicates.
 * @param {object} [context.selectedPaymentMethod] - The selected payment method.
 * @param {string} [context.paymentEngine] - The payment engine identifier.
 * @param {object} [context.env] - Environment configuration.
 * @returns {object|null} The matching handler, or null if none found.
 */
function getHandler(paymentType, context) {
  const handlers = registry.get(paymentType) || [];

  const specific = handlers
    .filter((h) => h.canHandle)
    .sort((a, b) => (b.priority ?? 0) - (a.priority ?? 0))
    .find((h) => h.canHandle(context));
  if (specific) return specific;

  const fallback = handlers.find((h) => !h.canHandle);
  return fallback || null;
}

export { registerHandler, getHandler, registry };
